import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import {
  BarList, Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtCell, panelGet, panelPost,
} from './kit';
import OceanMatching from './ocean/Matching';
import OceanSearchEngine from './ocean/SearchEngine';
import FeatureAccessCard from './FeatureAccessCard';
import { NotEnabled, tabKey, useAdminAccess } from './adminAccess';
import { type AdminTargetIn } from './adminNav';

/**
 * Ocean group — the company problem statements mined out of the Common Crawl
 * (Ocean Search / Cosmos), browsable end to end from the admin panel.
 *
 * Nothing here touches Postgres. The whole dataset lives in one DuckDB file
 * under ocean_data/ on the server (scripts/sync_ocean_data.py builds it from
 * the S3 exports), and every read goes through the admin dispatcher's "ocean"
 * section (admin_ocean.py) against that file, read-only. The two raw routes
 * exist only because the dispatcher wraps every answer in a JSON envelope,
 * which cannot carry a 100 MB workbook or a 400k-row CSV.
 *
 * Contract (admin_ocean.py):
 *   GET overview                    ready, data_dir, counts{companies, problems, evidence, pages,
 *                                   domains, tags, pivots, datasets}, meta{headline, headline_live,
 *                                   pipeline, spend, caveats, aws, built_at, source_files, ...},
 *                                   summary (cosmos_part2_summary.json), facets{industries,
 *                                   business_models, bands, countries, page_types, statuses}, files
 *   GET companies|problems|evidence|pages|domains
 *                                   ?q, <filters>, page, per_page, sort, direction
 *                                   -> rows, total, page, per_page, pages, facets{name: [{value, n}]}
 *   GET company?domain=             company, domain_info, problems, evidence (capped), evidence_total,
 *                                   evidence_truncated, pages_summary
 *   GET pivots / pivot?key=         the registry / one pivot's rows (+ file = its CSV under pivots/)
 *   GET datasets / files            the S3+Athena+local inventory (+ disk) / what is under ocean_data/
 *   GET presign?key=|s3_key=        { url, expires_in } for an S3-only artefact
 *   GET schema / tags               DuckDB tables + columns / the 64-tag taxonomy with counts
 *   POST sql {query, limit}         columns, rows (arrays), row_count, truncated, elapsed_ms
 *   Raw: GET /api/v1/admin/ocean/export?view=&format=csv|xlsx&<same filters>  (view=pivot&key=,
 *        view=sql&query=&limit=, view=tags)
 *        GET /api/v1/admin/ocean/file?name=<path under ocean_data/>
 * A handler that hits a predictable failure answers 200 with {error} (or
 * {ready:false, message}) rather than 500 — oceanGet() below turns both into
 * the error string the kit's Msg shows.
 */

const S = 'ocean';
const EXPORT_PATH = '/api/v1/admin/ocean/export';
const FILE_PATH = '/api/v1/admin/ocean/file';

// 'COSMOS matching' is the OCEAN member page's semantic matching (ocean/Matching.tsx,
// section ocean_match), not the DuckDB dataset the other tabs browse. It is a
// controlled sub-tab ("ocean.cosmos_matching", admin/adminAccess.tsx): hidden
// from other admins until a super admin enables it in Access control.
// 'Feature access' is who may open the OCEAN member page (FeatureAccessCard,
// OCEAN's row; Backend/feature_access.py). Super admins only
// ("ocean.feature_access", never delegated). It needs no dataset, so it stays
// reachable while the dataset loads, is not built or fails to open.
// 'Search engine' is the OCEAN member page's search (ocean/SearchEngine.tsx,
// section ocean_search): its keyword and semantic indexes and settings. A
// controlled sub-tab ("ocean.search_engine") like COSMOS matching, and like
// Feature access it needs no dataset.
const FEATURE_ACCESS = 'Feature access';
const SEARCH_ENGINE = 'Search engine';
const TABS = ['Overview', 'Companies', 'Problems', 'Evidence', 'Pages', 'Domains',
  'Dashboards', 'Data & files', 'Schema', 'SQL', 'COSMOS matching', SEARCH_ENGINE, FEATURE_ACCESS];
/* /admin?group=ocean&tab=<snake_case of a tab name> (tabKey), e.g. tab=cosmos_matching, tab=feature_access. */

const PAGE_SIZES = ['50', '100', '200'];

// ---------------------------------------------------------------------------
// Small helpers — formatting and shape normalisation
// ---------------------------------------------------------------------------
const uid = () => apiService.getStoredUserId() || '';

const n = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const x = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(x)) return String(v);
  return Number.isInteger(x) ? x.toLocaleString() : x.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const fmtBytes = (b: any): string => {
  const x = Number(b);
  if (b === null || b === undefined || b === '' || Number.isNaN(x)) return '—';
  if (x < 1024) return `${x} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  if (x < 1024 * 1024 * 1024) return `${(x / 1024 / 1024).toFixed(1)} MB`;
  return `${(x / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const labelize = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const isUrl = (v: any) => typeof v === 'string' && /^https?:\/\//i.test(v.trim());
const basename = (p: string) => (p || '').split('/').filter(Boolean).pop() || p;
const truthy = (v: any) => v === true || v === 1 || v === '1' || v === 'true';

/** First present value among candidate keys. */
const pick = (o: any, ...keys: string[]): any => {
  if (!o || typeof o !== 'object') return undefined;
  for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};

type Rows = { rows: Record<string, any>[]; columns: string[]; total: number };

/** Rows come as objects from the list handlers and as arrays aligned to
 *  `columns` from the SQL console; one reader for both. */
const normRows = (d: any): Rows => {
  if (!d) return { rows: [], columns: [], total: 0 };
  const src: any[] = Array.isArray(d) ? d : (pick(d, 'rows', 'items') || []);
  let columns: string[] = Array.isArray(d?.columns)
    ? d.columns.map((c: any) => (typeof c === 'string' ? c : (c?.name ?? String(c))))
    : [];
  let rows: Record<string, any>[];
  if (src.length && Array.isArray(src[0])) {
    rows = src.map(r => Object.fromEntries(columns.map((c, i) => [c, r[i]])));
  } else {
    rows = src as Record<string, any>[];
  }
  if (!columns.length && rows[0]) columns = Object.keys(rows[0]);
  const total = Number(pick(d, 'total', 'count', 'row_count') ?? rows.length) || 0;
  return { rows, columns, total };
};

type Bar = { label: string; value: number };

/** The facet shape is [{value, n}]; the summary JSON has {label: number} and
 *  {label: {total, problems}}. Bars want one shape. */
const toBars = (x: any, limit = 25): Bar[] => {
  if (!x) return [];
  const num = (v: any): number => {
    if (typeof v === 'number') return v;
    if (v && typeof v === 'object') return Number(pick(v, 'n', 'count', 'total', 'value', 'problems')) || 0;
    const f = Number(v); return Number.isNaN(f) ? 0 : f;
  };
  let out: Bar[] = [];
  if (Array.isArray(x)) {
    out = x.map((it: any) => {
      if (it && typeof it === 'object') {
        const label = pick(it, 'value', 'label', 'name', 'key', 'tag');
        const value = pick(it, 'n', 'count', 'problems', 'companies', 'total');
        return { label: String(label ?? '—'), value: num(value) };
      }
      return { label: String(it), value: 0 };
    });
  } else if (typeof x === 'object') {
    out = Object.entries(x).map(([k, v]) => ({ label: k, value: num(v) }));
  }
  return out.filter(b => b.label !== 'undefined' && b.label !== 'null' && b.label !== '')
    .sort((a, b) => b.value - a.value).slice(0, limit);
};

/** Facet values as strings: [{value, n}] or plain strings. */
const facetValues = (x: any): string[] => {
  if (!Array.isArray(x)) return [];
  return x.map((it: any) => (it && typeof it === 'object' ? pick(it, 'value', 'label') : it))
    .filter(v => v !== null && v !== undefined && v !== '').map(v => String(v));
};

const dropEmpty = (p: Record<string, any>): Record<string, string | number> =>
  Object.fromEntries(Object.entries(p)
    .filter(([, v]) => v !== '' && v !== undefined && v !== null)
    .map(([k, v]) => [k, typeof v === 'number' ? v : String(v)]));

const saveBlob = (blob: Blob, name: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** Download through a raw admin route. Returns '' on success, else the error. */
const fetchFile = async (path: string, params: Record<string, any>, fallbackName: string): Promise<string> => {
  const { blob, filename, error } = await apiService.adminFetchFile(uid(), path, dropEmpty(params));
  if (error || !blob) return error || 'Download failed';
  saveBlob(blob, filename || fallbackName);
  return '';
};

// ---------------------------------------------------------------------------
// Fetch layer — the guard's 200-with-{error} answers become errors here, and a
// last-wins hook keeps a slow page from overwriting the one that was asked for.
// ---------------------------------------------------------------------------
type Resp = { data: any; error: string };

const oceanGet = async (action: string, params: Record<string, any> = {}): Promise<Resp> => {
  const r = await panelGet(S, action, dropEmpty(params));
  if (r.error) return r;
  const d = r.data;
  if (d && typeof d === 'object') {
    if (d.ready === false) return { data: d, error: d.message || 'Ocean data is not ready' };
    if (typeof d.error === 'string' && d.error) return { data: d, error: d.error };
  }
  return r;
};

function useOcean<T = any>(fetcher: () => Promise<Resp>, deps: any[], enabled = true) {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string }>(
    { data: null, loading: enabled, error: '' });
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!enabled) { setState({ data: null, loading: false, error: '' }); return; }
    const mine = ++seq.current;
    setState(s => ({ ...s, loading: true, error: '' }));
    const { data, error } = await fetcher();
    if (mine !== seq.current) return;
    setState({ data, loading: false, error });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

// The 64-tag taxonomy is the same for every view and never changes between
// builds, so it is fetched once per session and shared by every tag Select.
let tagsPromise: Promise<string[]> | null = null;
const loadTags = (): Promise<string[]> => {
  if (!tagsPromise) {
    tagsPromise = oceanGet('tags').then(r => normRows(r.data).rows.map(t => String(t.tag)).filter(Boolean))
      .catch(() => [] as string[]);
  }
  return tagsPromise;
};

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-grayscale-1100 whitespace-nowrap">{label}</span>
    <span className="text-grayscale-1300 font-medium text-right break-all">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

const Ext: React.FC<{ href: string; children?: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer"
     className="text-primary-500 underline underline-offset-2 break-all"
     onClick={e => e.stopPropagation()}>
    {children ?? href}
  </a>
);

const Flag: React.FC<{ on: any; children: React.ReactNode }> = ({ on, children }) => (
  <span className={`text-xs rounded-full px-2 py-0.5 border ${truthy(on)
    ? 'text-green-700 bg-green-50 border-green-200'
    : 'text-grayscale-1100 bg-grayscale-100 border-grayscale-300'}`}>
    {truthy(on) ? '✓ ' : '— '}{children}
  </span>
);

const Drawer: React.FC<{
  title: React.ReactNode; subtitle?: React.ReactNode; onClose: () => void;
  right?: React.ReactNode; children: React.ReactNode;
}> = ({ title, subtitle, onClose, right, children }) => (
  <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
    <div className="bg-grayscale-100 w-full max-w-3xl h-full overflow-y-auto p-6 flex flex-col gap-4"
         onClick={e => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk break-words">{title}</h2>
          {subtitle && <div className="text-xs text-grayscale-1100 break-words">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          {right}
          <Btn onClick={onClose}>Close</Btn>
        </div>
      </div>
      {children}
    </div>
  </div>
);

/** Every field of one row, long text wrapped, links clickable, the evidence
 *  column split back into the snippets it was joined from. */
const FieldList: React.FC<{ row: Record<string, any>; skip?: string[] }> = ({ row, skip = [] }) => {
  const cols = Object.keys(row).filter(c => !skip.includes(c) && !c.startsWith('_'));
  return (
    <div className="flex flex-col divide-y divide-grayscale-200">
      {cols.map(c => {
        const v = row[c];
        const snippets = c === 'problem_evidence' && typeof v === 'string' && v.includes(' || ')
          ? v.split(' || ').map(s => s.trim()).filter(Boolean) : null;
        return (
          <div key={c} className="py-2 flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-mono">{c}</div>
            {snippets ? (
              <ul className="list-disc pl-5 text-sm text-grayscale-1300 flex flex-col gap-1">
                {snippets.map((s, i) => <li key={i} className="whitespace-pre-wrap break-words">{s}</li>)}
              </ul>
            ) : isUrl(v) ? (
              <Ext href={String(v).trim()} />
            ) : c === 'domain' && v ? (
              <Ext href={`https://${v}`}>{String(v)}</Ext>
            ) : (
              <div className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">{fmtCell(c, v)}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** CSV + XLSX of whatever `params` describes, through the raw export route. */
const ExportPair: React.FC<{ params: Record<string, any>; name: string; small?: boolean; title?: string }> =
  ({ params, name, small = true, title }) => {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const go = async (format: 'csv' | 'xlsx') => {
    setBusy(format); setErr('');
    const e = await fetchFile(EXPORT_PATH, { ...params, format }, `${name}.${format}`);
    setBusy(''); if (e) setErr(e);
  };
  const hint = title || 'Every row matching the current filters, not just this page';
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Btn small={small} disabled={!!busy} onClick={() => go('csv')} title={hint}>
        {busy === 'csv' ? 'Preparing…' : 'Export CSV'}
      </Btn>
      <Btn small={small} disabled={!!busy} onClick={() => go('xlsx')} title={hint}>
        {busy === 'xlsx' ? 'Preparing…' : 'Export XLSX'}
      </Btn>
      {err && <span className="text-xs text-red-600">{err}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// View definitions — the five browsable tables, their columns and filters.
// Filter keys are the query params admin_ocean._VIEWS accepts; `facet` names
// the facet each list answer carries (or the overview facet that seeds it).
// ---------------------------------------------------------------------------
type Filter = {
  key: string; label: string; kind: 'select' | 'text' | 'number' | 'bool' | 'contact' | 'tag';
  facet?: string; placeholder?: string;
};

type DrawerKind = 'company' | 'problem' | 'evidence' | 'page' | 'domain';

type ViewDef = {
  view: string; title: string; blurb: string; columns: string[]; labels: Record<string, string>;
  filters: Filter[]; sortable: string[]; sort: { key: string; dir: 'asc' | 'desc' }; drawer: DrawerKind;
};

const F = {
  industry: { key: 'industry', label: 'Industry', kind: 'select', facet: 'industry' } as Filter,
  model: { key: 'business_model', label: 'Business model', kind: 'select', facet: 'business_model' } as Filter,
  country: { key: 'country', label: 'HQ country', kind: 'select', facet: 'country' } as Filter,
  pageType: { key: 'page_type', label: 'Page type', kind: 'select', facet: 'page_type' } as Filter,
  tag: { key: 'tag', label: 'Technical tag', kind: 'tag' } as Filter,
  band: { key: 'band', label: 'Problem band', kind: 'select', facet: 'band' } as Filter,
  tld: { key: 'tld', label: 'TLD', kind: 'select', facet: 'tld', placeholder: 'e.g. com' } as Filter,
  domain: { key: 'domain', label: 'Domain', kind: 'text', placeholder: 'exact domain' } as Filter,
  minConf: { key: 'min_confidence', label: 'Min confidence', kind: 'number', placeholder: '0–100' } as Filter,
  contact: { key: 'contact', label: 'Contact', kind: 'contact' } as Filter,
  year: { key: 'year', label: 'Content year', kind: 'number', placeholder: 'e.g. 2025' } as Filter,
  cluster: { key: 'cluster_id', label: 'Cluster id', kind: 'number', placeholder: 'with a domain' } as Filter,
};

const VIEWS: Record<string, ViewDef> = {
  Companies: {
    view: 'companies', title: 'Companies', drawer: 'company',
    blurb: 'One row per company (registered domain) with its deduplicated problem count. Search matches domain and name.',
    columns: ['domain', 'company_name', 'industry', 'business_model', 'hq_country_norm', 'unique_problems',
      'raw_statements', 'avg_confidence', 'problem_band', 'top_technical_tags', 'page_types', 'distinct_urls',
      'has_email', 'has_phone', 'has_linkedin', 'has_contact_person', 'tld'],
    labels: { hq_country_norm: 'HQ country', unique_problems: 'Unique problems', raw_statements: 'Raw statements',
      avg_confidence: 'Avg conf.', problem_band: 'Band', top_technical_tags: 'Top tags', distinct_urls: 'URLs',
      has_email: 'Email', has_phone: 'Phone', has_linkedin: 'LinkedIn', has_contact_person: 'Contact', tld: 'TLD' },
    filters: [F.industry, F.model, F.country, F.band, F.tag, F.pageType, F.tld,
      { key: 'min_problems', label: 'Min unique problems', kind: 'number', placeholder: 'e.g. 5' },
      F.minConf, F.contact, F.domain],
    sortable: ['unique_problems', 'raw_statements', 'avg_confidence', 'company_name', 'domain', 'distinct_urls'],
    sort: { key: 'unique_problems', dir: 'desc' },
  },
  Problems: {
    view: 'problems', title: 'Unique problems', drawer: 'problem',
    blurb: 'One row per (company, cluster): the canonical wording of a problem and how many pages said it. Search matches the statement.',
    columns: ['domain', 'company_name', 'cluster_id', 'problem_statement', 'pages_mentioning', 'wording_variants',
      'avg_confidence', 'technical_tags', 'page_types', 'industry', 'business_model', 'hq_country_norm'],
    labels: { cluster_id: 'Cluster', pages_mentioning: 'Pages', wording_variants: 'Variants', avg_confidence: 'Avg conf.',
      hq_country_norm: 'HQ country' },
    filters: [F.industry, F.model, F.country, F.tag, F.pageType,
      { key: 'min_pages', label: 'Min pages mentioning', kind: 'number', placeholder: 'e.g. 2' },
      F.minConf, F.domain, F.cluster],
    sortable: ['pages_mentioning', 'avg_confidence', 'wording_variants', 'domain'],
    sort: { key: 'pages_mentioning', dir: 'desc' },
  },
  Evidence: {
    view: 'evidence', title: 'Evidence (row-level statements)', drawer: 'evidence',
    blurb: 'One row per (company, page): the statement as written on that page, with its source URL. Search matches statement and evidence text.',
    columns: ['domain', 'company_name', 'cluster_id', 'problem_statement', 'problem_confidence', 'page_type', 'url',
      'technical_tags', 'industry', 'business_model', 'hq_country_norm', 'content_year', 'problem_evidence'],
    labels: { cluster_id: 'Cluster', problem_confidence: 'Conf.', hq_country_norm: 'HQ country',
      content_year: 'Year', problem_evidence: 'Evidence', url: 'URL' },
    filters: [F.industry, F.model, F.pageType, F.country, F.tag, F.minConf, F.year, F.domain, F.cluster],
    sortable: ['problem_confidence', 'content_year', 'domain', 'cluster_id'],
    sort: { key: 'problem_confidence', dir: 'desc' },
  },
  Pages: {
    view: 'pages', title: 'Pages (every page classified)', drawer: 'page',
    blurb: 'All 1.2M pages Cosmos looked at, problems or not, with contact fields and status. Search matches domain, name and URL.',
    columns: ['domain', 'company_name', 'page_type', 'business_model', 'industry', 'hq_country_norm', 'status',
      'is_problem', 'problem_statement', 'problem_confidence', 'confidence_bucket', 'contact_available',
      'has_email', 'has_phone', 'has_linkedin', 'has_contact_person', 'content_year', 'url'],
    labels: { hq_country_norm: 'HQ country', is_problem: 'Problem?', problem_confidence: 'Conf.',
      confidence_bucket: 'Bucket', contact_available: 'Contact?', has_email: 'Email', has_phone: 'Phone',
      has_linkedin: 'LinkedIn', has_contact_person: 'Person', content_year: 'Year', url: 'URL' },
    filters: [F.pageType, F.model, F.industry, F.country,
      { key: 'status', label: 'Status', kind: 'select', facet: 'status' },
      { key: 'confidence_bucket', label: 'Confidence bucket', kind: 'select', facet: 'confidence_bucket', placeholder: 'e.g. 80-100' },
      { key: 'is_problem', label: 'Has problem', kind: 'bool' },
      F.tag, F.minConf, F.year, F.contact, F.domain],
    sortable: ['problem_confidence', 'inference_confidence', 'content_year', 'domain', 'page_type', 'status'],
    sort: { key: 'problem_confidence', dir: 'desc' },
  },
  Domains: {
    view: 'domains', title: 'Domains (batch-1 classification)', drawer: 'domain',
    blurb: 'The 1.52M domains classified for business model and industry, whether or not any problem was found for them.',
    columns: ['domain', 'industry', 'industry_2', 'business_model', 'confidence', 'url_source_type',
      'decay_bucket', 'tld', 'has_problems'],
    labels: { industry_2: 'Industry 2', url_source_type: 'URL source', decay_bucket: 'Decay', tld: 'TLD',
      has_problems: 'Problems?' },
    filters: [F.industry, F.model, F.tld,
      { key: 'url_source_type', label: 'URL source', kind: 'select', facet: 'url_source_type' },
      { key: 'decay_bucket', label: 'Decay bucket', kind: 'select', facet: 'decay_bucket' },
      { key: 'has_problems', label: 'Has problems', kind: 'bool' },
      F.minConf, F.domain],
    sortable: ['domain', 'industry', 'business_model', 'confidence', 'tld', 'has_problems'],
    sort: { key: 'domain', dir: 'asc' },
  },
};

const BOOL_OPTS = ['', '1', '0'];
const BOOL_LABELS = { '': 'Any', '1': 'Yes', '0': 'No' };
// admin_ocean's `contact` filter: any of the four channels, one of them, or none.
const CONTACT_OPTS = ['', 'any', 'email', 'phone', 'linkedin', 'person', 'none'];
const CONTACT_LABELS = { '': 'Any', any: 'Has any contact', email: 'Has email', phone: 'Has phone',
  linkedin: 'Has LinkedIn', person: 'Has contact person', none: 'No contact at all' };

// Overview facet name -> the filter param it seeds in every list view.
const SEED_FACETS: Record<string, string> = {
  industries: 'industry', business_models: 'business_model', bands: 'band', countries: 'country',
  page_types: 'page_type', statuses: 'status',
};
// Small fixed vocabularies the server does not facet (pivot_tables README).
const STATIC_SEEDS: Record<string, string[]> = {
  confidence_bucket: ['0-20', '20-40', '40-60', '60-80', '80-100'],
};

type Preset = { tab: string; filters: Record<string, string>; n: number };
type DrawerState = { kind: DrawerKind; row: Record<string, any> } | null;

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------
const OceanPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const access = useAdminAccess();
  // The tabs this admin may open; a deep link to another one lands on `tab`
  // anyway and shows the "not enabled" state below instead of the content.
  const tabs = TABS.filter(t => access.canTab('ocean', tabKey(t)));
  const [tab, setTab] = useState(TABS[0]);
  useEffect(() => {
    if (!target?.tab) return;
    const want = tabKey(String(target.tab));
    const hit = TABS.find(t => tabKey(t) === want || t === target.tab);
    if (hit) setTab(hit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  // A click on an Overview bar lands on a list tab with that filter already
  // applied; `n` bumps so the same filter twice still remounts the list.
  const [preset, setPreset] = useState<Preset>({ tab: '', filters: {}, n: 0 });
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const ov = useOcean(() => oceanGet('overview'), []);

  const jump = (target: string, filters: Record<string, string>) => {
    setPreset({ tab: target, filters, n: preset.n + 1 });
    setTab(target);
  };
  const open = (kind: DrawerKind, row: Record<string, any>) => setDrawer({ kind, row });

  const o = ov.data || {};
  // Option lists for the Selects that the list answers do not facet (country,
  // status on every view): the overview already counted them over the whole set.
  const seeds = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [facet, param] of Object.entries(SEED_FACETS)) out[param] = facetValues(o.facets?.[facet]);
    return { ...STATIC_SEEDS, ...out };
  }, [o.facets]);

  // A sub-tab this admin may not open needs no dataset either.
  if (!tabs.includes(tab)) {
    return (
      <div className="flex flex-col gap-4">
        <SubTabs tabs={tabs} active="" onChange={setTab} />
        <NotEnabled what={tab} compact />
      </div>
    );
  }
  // Feature access needs no dataset: never behind its loading or errors, and always rendered in this
  // one spot (same element, same place, same key), so the card keeps its unsaved choices, an open
  // confirmation or a save in progress when the dataset finishes loading underneath it.
  const featureTab = tabs.includes(FEATURE_ACCESS) ? [FEATURE_ACCESS] : [];
  if (tab === SEARCH_ENGINE) {
    const strip = (o.ready === false || ov.loading || ov.error) ? [SEARCH_ENGINE, ...featureTab] : tabs;
    return (
      <div className="flex flex-col gap-4">
        <SubTabs tabs={strip} active={SEARCH_ENGINE} onChange={setTab} />
        <OceanSearchEngine />
      </div>
    );
  }
  if (tab === FEATURE_ACCESS) {
    // 'Data & files' is the one dataset tab left when it is not built.
    const strip = o.ready === false ? ['Data & files', FEATURE_ACCESS] : (ov.loading || ov.error) ? featureTab : tabs;
    return (
      <div className="flex flex-col gap-4">
        <SubTabs tabs={strip} active={FEATURE_ACCESS} onChange={setTab} />
        <FeatureAccessCard key="ocean-feature-access" only={['ocean']} />
      </div>
    );
  }
  if (ov.loading) return <Loading label="Opening the Ocean dataset…" />;
  if (o.ready === false) {
    // No database yet (first sync still running, or a failed build): the
    // `files` handler is deliberately unguarded so this is where an operator
    // watches what has landed under ocean_data/ — keep that tab reachable.
    const only = 'Data & files';
    return (
      <div className="flex flex-col gap-4">
        <Msg kind="error">
          {o.message || ov.error || 'The Ocean dataset is not built on this server yet.'}
          {o.data_dir && <span className="font-mono"> — {o.data_dir}</span>}
        </Msg>
        <SubTabs tabs={[only, ...featureTab]} active={only} onChange={setTab} />
        <DataFiles aws={{}} manifest={o.files} />
      </div>
    );
  }
  if (ov.error) {
    return (
      <div className="flex flex-col gap-4">
        <Msg kind="error">{ov.error}</Msg>
        {featureTab.length > 0 && <SubTabs tabs={featureTab} active="" onChange={setTab} />}
      </div>
    );
  }

  const def = VIEWS[tab];
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'Overview' && <Overview o={o} reload={ov.reload} onJump={jump} />}
      {def && (
        <ListView key={`${tab}-${preset.tab === tab ? preset.n : 0}`} def={def} seeds={seeds}
          initial={preset.tab === tab ? preset.filters : {}}
          onOpen={row => open(def.drawer, row)} />
      )}
      {tab === 'Dashboards' && <Dashboards />}
      {tab === 'Data & files' && <DataFiles aws={o.meta?.aws || {}} manifest={o.files} />}
      {tab === 'Schema' && <Schema />}
      {tab === 'SQL' && <SqlConsole />}
      {tab === 'COSMOS matching' && <OceanMatching />}
      {drawer?.kind === 'company' && (
        <CompanyDrawer row={drawer.row} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'problem' && (
        <ProblemDrawer row={drawer.row} onClose={() => setDrawer(null)}
          onCompany={r => open('company', r)} />
      )}
      {(drawer?.kind === 'evidence' || drawer?.kind === 'page' || drawer?.kind === 'domain') && (
        <RowDrawer kind={drawer.kind} row={drawer.row} onClose={() => setDrawer(null)}
          onCompany={r => open('company', r)} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 1. Overview — headline numbers, the pipeline, spend, breakdowns, caveats
// ---------------------------------------------------------------------------
const Overview: React.FC<{ o: any; reload: () => void; onJump: (tab: string, f: Record<string, string>) => void }> =
  ({ o, reload, onJump }) => {
  const meta = o.meta || {};
  const counts = o.counts || {};
  // headline = the handover's §1 table as transcribed; headline_live = the same
  // numbers counted from the DuckDB file at build time. Live wins when present.
  const live = meta.headline_live || {};
  const head = meta.headline || {};
  const aws = meta.aws || {};
  const facets = o.facets || {};
  const summary = o.summary || meta.summary || {};

  const tags = useOcean(() => oceanGet('tags'), []);
  const tagBars = useMemo(() => {
    const rows = normRows(tags.data).rows;
    return rows.map(r => ({ label: String(r.tag), value: Number(r.problems) || 0 }))
      .sort((a, b) => b.value - a.value).slice(0, 20);
  }, [tags.data]);

  const pipeline: any[] = Array.isArray(meta.pipeline) ? meta.pipeline : [];
  const caveats: string[] = Array.isArray(meta.caveats) ? meta.caveats.map(String) : [];
  const spendRows = Object.entries(meta.spend || {}).filter(([, v]) => typeof v === 'number') as [string, number][];
  const spendNotes = Object.entries(meta.spend || {}).filter(([, v]) => typeof v !== 'number');
  const sources = Object.entries(meta.source_files || {}).map(([name, v]: [string, any]) => ({ name, ...(v || {}) }));
  const headRows = Object.entries(head).filter(([k]) => k !== 'source');

  const bars = {
    industries: toBars(facets.industries ?? summary.by_industry),
    models: toBars(facets.business_models ?? summary.by_business_model),
    bands: toBars(facets.bands, 12),
    countries: toBars(facets.countries ?? summary.top_countries, 25),
    pageTypes: toBars(facets.page_types ?? summary.by_page_type, 12),
    statuses: toBars(facets.statuses ?? summary.status_breakdown, 8),
  };
  // Bands are ordinal (1-3 … 17+); the server sends them in band order and
  // toBars sorts by value, so put them back in order here.
  bars.bands.sort((a, b) => (parseInt(a.label, 10) || 0) - (parseInt(b.label, 10) || 0));

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Companies" value={n(counts.companies ?? live.companies)} hint="with ≥1 problem" />
        <KPI label="Unique problems" value={n(counts.problems ?? live.problems)} hint="after dedup" />
        <KPI label="Evidence rows" value={n(counts.evidence ?? live.evidence_rows)} hint="row-level statements" />
        <KPI label="Pages classified" value={n(counts.pages ?? live.pages)} />
        <KPI label="Domains classified" value={n(counts.domains ?? live.domains)} hint="batch-1" />
        <KPI label="Industries" value={n(live.industries ?? head.industries)} />
        <KPI label="HQ countries" value={n(live.hq_countries ?? head.hq_countries)} />
        <KPI label="Avg confidence" value={n(live.avg_problem_confidence ?? head.avg_problem_confidence)} hint="over evidence rows" />
      </KPIRow>

      <Section title="Pipeline — Common Crawl to problem statements"
        right={<Btn small onClick={reload}>Refresh</Btn>}>
        {pipeline.length ? (
          <div className="overflow-auto rounded-lg border border-grayscale-300">
            <table className="w-full text-sm">
              <thead className="bg-grayscale-200">
                <tr>
                  {['Stage', 'What', 'Counts', 'S3 prefixes', 'Athena / DuckDB'].map(h => (
                    <th key={h} className="text-left font-grotesk text-xs px-3 py-2 text-grayscale-1200 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pipeline.map((s: any, i: number) => (
                  <tr key={i} className="border-t border-grayscale-200 align-top">
                    <td className="px-3 py-1.5 text-grayscale-1300 whitespace-nowrap">{s.stage ?? i + 1}</td>
                    <td className="px-3 py-1.5 text-grayscale-1300 min-w-[14rem]">
                      {s.name}
                      {s.detail && <div className="text-xs text-grayscale-1100">{s.detail}</div>}
                    </td>
                    <td className="px-3 py-1.5 text-grayscale-1300 text-xs">
                      {Object.entries(s.counts || {}).map(([k, v]) => (
                        <div key={k}><span className="text-grayscale-1100">{labelize(k)}</span> {n(v)}</div>
                      ))}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-grayscale-1300">
                      {(s.s3_prefixes || []).map((p: string) => <div key={p}>{p}</div>)}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-grayscale-1300">
                      {(s.athena || []).map((t: string) => <div key={t}>athena: {t}</div>)}
                      {(s.duckdb_tables || []).map((t: string) => <div key={t}>duckdb: {t}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-grayscale-1100">No pipeline metadata in this build.</p>}
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Spend to date">
          {spendRows.length ? (
            <div className="flex flex-col divide-y divide-grayscale-200">
              {spendRows.map(([k, v]) => (
                <Fact key={k} label={labelize(k.replace(/_usd(_approx)?$/, ''))}
                  value={`$${v.toLocaleString()}${/approx/.test(k) ? ' (approx.)' : ''}`} />
              ))}
            </div>
          ) : <p className="text-sm text-grayscale-1100">No spend metadata in this build.</p>}
          {spendNotes.map(([k, v]) => (
            <div key={k} className="text-xs text-grayscale-1100">
              <span className="font-medium">{labelize(k)}:</span>{' '}
              {typeof v === 'string' ? v : JSON.stringify(v)}
            </div>
          ))}
        </Section>
        <Section title="Headline"
          right={head.source ? <span className="text-xs text-grayscale-1100">{head.source}</span> : undefined}>
          {headRows.length ? (
            <div className="flex flex-col divide-y divide-grayscale-200">
              {headRows.map(([k, v]) => (
                <Fact key={k} label={labelize(k)}
                  value={<>{n(v)}{live[k] !== undefined && live[k] !== v ? <span className="text-xs text-grayscale-1100"> · live {n(live[k])}</span> : null}</>} />
              ))}
            </div>
          ) : <p className="text-sm text-grayscale-1100">No headline table in this build.</p>}
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {!!bars.industries.length && (
          <Section title="Companies by industry">
            <BarList items={bars.industries} onSelect={l => onJump('Companies', { industry: l })} />
          </Section>
        )}
        {!!bars.models.length && (
          <Section title="Companies by business model">
            <BarList items={bars.models} onSelect={l => onJump('Companies', { business_model: l })} />
          </Section>
        )}
        {!!bars.bands.length && (
          <Section title="Companies by problem-count band">
            <BarList items={bars.bands} onSelect={l => onJump('Companies', { band: l })} />
          </Section>
        )}
        {!!bars.countries.length && (
          <Section title="Companies by HQ country (top 25)">
            <BarList items={bars.countries} onSelect={l => onJump('Companies', { country: l })} />
          </Section>
        )}
        {!!bars.pageTypes.length && (
          <Section title="Evidence rows by page type">
            <BarList items={bars.pageTypes} onSelect={l => onJump('Evidence', { page_type: l })} />
          </Section>
        )}
        {!!bars.statuses.length && (
          <Section title="Pages by Cosmos status">
            <BarList items={bars.statuses} onSelect={l => onJump('Pages', { status: l })} />
          </Section>
        )}
        <Section title="Problems by technical tag (top 20 of 64)"
          right={<ExportPair params={{ view: 'tags' }} name="ocean_tags" title="The full taxonomy with counts" />}>
          {tags.error && <Msg kind="error">{tags.error}</Msg>}
          {tags.loading ? <Loading /> : tagBars.length
            ? <BarList items={tagBars} onSelect={l => onJump('Problems', { tag: l })} />
            : <p className="text-sm text-grayscale-1100">No tag counts in this build.</p>}
        </Section>
        <p className="text-xs text-grayscale-1100 md:col-span-2 -mt-2">
          Click a bar to open the matching tab filtered to it.
        </p>
      </div>

      {!!caveats.length && (
        <Section title="Known caveats">
          <ul className="list-disc pl-5 text-sm text-grayscale-1300 flex flex-col gap-1">
            {caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </Section>
      )}

      <Section title="This build"
        right={<span className="text-xs text-grayscale-1100">
          built {meta.built_at ? fmtCell('built_at', meta.built_at) : '—'}
          {o.data_dir && <> · <span className="font-mono">{o.data_dir}</span></>}
        </span>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          AWS account {aws.account ?? '—'} · {aws.region ?? '—'} · bucket{' '}
          <span className="font-mono">{aws.bucket ?? '—'}</span>
          {aws.athena_database && <> · Athena <span className="font-mono">{aws.athena_database}</span></>}
          {' · '}{n(counts.tags)} tags · {n(counts.pivots)} pivots · {n(counts.datasets)} inventory entries
        </p>
        {sources.length ? (
          <DataTable rows={sources} columns={['name', 'rows', 'bytes', 'mtime']} maxHeight="20rem"
            labels={{ mtime: 'Modified' }}
            render={{ bytes: r => fmtBytes(r.bytes), rows: r => n(r.rows), mtime: r => fmtCell('mtime_at', r.mtime),
              name: r => <span className="font-mono text-xs">{r.name}</span> }} />
        ) : <p className="text-sm text-grayscale-1100">No source-file manifest in this build.</p>}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. ListView — one server-paged, filterable, sortable table per view
// ---------------------------------------------------------------------------
const ListView: React.FC<{
  def: ViewDef; seeds: Record<string, string[]>; initial: Record<string, string>;
  onOpen: (row: Record<string, any>) => void;
}> = ({ def, seeds, initial, onOpen }) => {
  // Draft vs applied: typing must not fire a query per keystroke on a
  // 1.2M-row table, so text lands on Apply (or Enter); Selects apply at once.
  const [qDraft, setQDraft] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [applied, setApplied] = useState<{ q: string; f: Record<string, string> }>({ q: '', f: initial });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PAGE_SIZES[0]);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(def.sort);
  // Exports carry a sort only once the user has picked one: admin_ocean
  // streams an unsorted export in table order (first byte at once), while a
  // sorted evidence/pages export materialises 700-900 MB before it starts.
  const [sortTouched, setSortTouched] = useState(false);
  const [sortHint, setSortHint] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => { let on = true; loadTags().then(t => { if (on) setTags(t); }); return () => { on = false; }; }, []);

  const params = useMemo(() => ({
    q: applied.q, ...applied.f, page, per_page: Number(perPage),
    sort: sort?.key || '', direction: sort?.key ? sort.dir : '',
  }), [applied, page, perPage, sort]);
  const d = useOcean(() => oceanGet(def.view, params), [def.view, params]);

  // Keep the last good answer while a page loads so the controls do not blink
  // away between pages. Facet options are the union of everything seen: the
  // server facets the CURRENT result, and a Select whose options shrank to the
  // one value already chosen would have no way back.
  const [last, setLast] = useState<any>(null);
  const [seen, setSeen] = useState<Record<string, string[]>>({});
  useEffect(() => {
    if (!d.data) return;
    setLast(d.data);
    const f = d.data.facets || {};
    setSeen(prev => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(f)) {
        const vals = facetValues(v);
        next[k] = Array.from(new Set([...(prev[k] || []), ...vals]));
      }
      return next;
    });
  }, [d.data]);
  const cur = d.data || last;
  const rows: Record<string, any>[] = cur?.rows || [];
  const total: number = Number(cur?.total ?? 0);
  const srvPer = Number(cur?.per_page ?? perPage);
  const from = total ? (page - 1) * srvPer + 1 : 0;
  const to = Math.min(total, (page - 1) * srvPer + rows.length);
  const pages = Number(cur?.pages) || Math.max(1, Math.ceil(total / srvPer));

  const columns = def.columns.filter(c => !rows[0] || c in rows[0]);

  const apply = (f: Record<string, string> = draft, q: string = qDraft) => {
    setApplied({ q: q.trim(), f: { ...applied.f, ...f } });
    setDraft({});
    setPage(1);
  };
  const setFilterNow = (k: string, v: string) => {
    setApplied(a => ({ ...a, f: { ...a.f, [k]: v } }));
    setPage(1);
  };
  const clearAll = () => { setQDraft(''); setDraft({}); setApplied({ q: '', f: {} }); setPage(1); };

  const optionsFor = (f: Filter): string[] => {
    if (f.kind === 'tag') return tags;
    const key = f.facet || f.key;
    const fromList = seen[key] || [];
    const fromSeed = seeds[key] || [];
    // Seeds first (whole-dataset order), then anything the list answers added.
    return Array.from(new Set([...fromSeed, ...fromList]));
  };

  const active = Object.entries(applied.f).filter(([, v]) => v !== '');
  const labelFor = (k: string) => def.filters.find(f => f.key === k)?.label || k;
  const valueLabel = (k: string, v: string) =>
    k === 'contact' ? (CONTACT_LABELS as any)[v] || v : (['is_problem', 'has_problems'].includes(k) ? (BOOL_LABELS as any)[v] || v : v);

  const onSortChange = (k: string, dir: 'asc' | 'desc' | '') => {
    if (k && !def.sortable.includes(k)) {
      setSortHint(`Sorting is available on: ${def.sortable.map(c => def.labels[c] || labelize(c)).join(', ')}.`);
      return;
    }
    setSortHint('');
    setSort(k && dir ? { key: k, dir } : null);
    setSortTouched(true);
    setPage(1);
  };

  const exportSort = sortTouched && sort?.key ? { sort: sort.key, direction: sort.dir } : {};
  const exportParams = { view: def.view, q: applied.q, ...applied.f, ...exportSort };

  const render: Record<string, (r: Record<string, any>) => React.ReactNode> = {
    domain: r => r.domain ? <Ext href={`https://${r.domain}`}>{r.domain}</Ext> : '—',
    url: r => isUrl(r.url) ? <Ext href={r.url}>{String(r.url).replace(/^https?:\/\//, '').slice(0, 60)}</Ext> : '—',
    avg_confidence: r => n(r.avg_confidence),
    problem_confidence: r => n(r.problem_confidence),
    confidence: r => n(r.confidence),
  };

  return (
    <div className="flex flex-col gap-4">
      <Section title={def.title}
        right={<ExportPair params={exportParams} name={`ocean_${def.view}`} />}>
        <p className="text-xs text-grayscale-1100 -mt-1">{def.blurb}</p>
        <div className="flex gap-2 items-end flex-wrap">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100 flex-1 min-w-[220px]">
            Search
            <div className="flex gap-2">
              <input value={qDraft} placeholder="substring, case-insensitive"
                onChange={e => setQDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm flex-1" />
              <Btn kind="primary" onClick={() => apply()}>Apply</Btn>
            </div>
          </label>
          {def.filters.map(f => {
            const value = draft[f.key] ?? applied.f[f.key] ?? '';
            const opts = (f.kind === 'select' || f.kind === 'tag') ? optionsFor(f) : [];
            return (
              <label key={f.key} className="flex flex-col gap-1 text-xs text-grayscale-1100">
                {f.label}
                {f.kind === 'bool' ? (
                  <Select value={value} onChange={v => setFilterNow(f.key, v)} options={BOOL_OPTS} labels={BOOL_LABELS} />
                ) : f.kind === 'contact' ? (
                  <Select value={value} onChange={v => setFilterNow(f.key, v)} options={CONTACT_OPTS} labels={CONTACT_LABELS} />
                ) : opts.length ? (
                  <Select value={value} onChange={v => setFilterNow(f.key, v)}
                    options={['', ...(value && !opts.includes(value) ? [value] : []), ...opts]}
                    labels={{ '': 'Any' }} />
                ) : (
                  <input value={value} placeholder={f.placeholder || 'any'}
                    type={f.kind === 'number' ? 'number' : 'text'}
                    onChange={e => setDraft(dr => ({ ...dr, [f.key]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                    className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-36" />
                )}
              </label>
            );
          })}
        </div>
        {(active.length > 0 || applied.q) && (
          <div className="flex items-center gap-2 flex-wrap">
            {applied.q && <Chip label="search" value={applied.q} onClear={() => { setQDraft(''); apply({}, ''); }} />}
            {active.map(([k, v]) => (
              <Chip key={k} label={labelFor(k)} value={valueLabel(k, v)} onClear={() => setFilterNow(k, '')} />
            ))}
            <button type="button" onClick={clearAll}
              className="text-xs text-grayscale-1100 underline underline-offset-2">clear all</button>
          </div>
        )}
        {Object.keys(draft).length > 0 && (
          <p className="text-xs text-amber-700">Typed filters apply on Enter or Apply.</p>
        )}
        {d.error && <Msg kind="error">{d.error}</Msg>}
        {sortHint && <p className="text-xs text-amber-700">{sortHint}</p>}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">Page size</span>
          <Select value={perPage} onChange={v => { setPerPage(v); setPage(1); }} options={PAGE_SIZES} />
          <Btn small disabled={page <= 1 || d.loading} onClick={() => setPage(p => Math.max(1, p - 1))}>← Previous</Btn>
          <Btn small disabled={page >= pages || d.loading} onClick={() => setPage(p => p + 1)}>Next →</Btn>
          <span className="text-xs text-grayscale-1100">
            {d.loading && !rows.length ? 'Loading…'
              : `${n(from)}–${n(to)} of ${n(total)} · page ${n(page)} of ${n(pages)}`}
            {cur?.elapsed_ms !== undefined && !d.loading && <> · {n(cur.elapsed_ms)} ms</>}
          </span>
          <span className="text-xs text-grayscale-1100 ml-auto">Open a row for every field.</span>
        </div>
        {d.loading && !rows.length ? <Loading /> : (
          <div className={d.loading ? 'opacity-60' : ''}>
            <DataTable rows={rows} columns={columns} labels={def.labels} maxHeight="34rem"
              csvName={`ocean_${def.view}_page${page}`} render={render}
              serverSort={sort} onSortChange={onSortChange}
              empty={active.length || applied.q ? 'No row matches these filters.' : 'No rows.'}
              actions={r => <Btn small onClick={() => onOpen(r)}>Open</Btn>} />
          </div>
        )}
        <p className="text-xs text-grayscale-1100">
          "Download CSV" in the table bar saves this page only; Export CSV / XLSX above saves every
          row matching the filters (CSV up to 500k rows, XLSX up to 200k — the file says when it was cut).
          Exports are in table order unless you sort a column; a sorted export of Evidence or Pages
          takes a while to start. Cells that begin with =, @, + or - (crawled text) are written as
          plain text in XLSX and prefixed with ' in CSV so a spreadsheet never evaluates them.
        </p>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3. Drawers — a company with its problems and evidence, a problem with its
//    evidence, and the plain "every field" view for evidence / pages / domains
// ---------------------------------------------------------------------------
const PROBLEM_COLS = ['problem_statement', 'pages_mentioning', 'wording_variants', 'avg_confidence', 'technical_tags', 'page_types'];
const EVIDENCE_COLS = ['problem_statement', 'problem_confidence', 'page_type', 'url', 'problem_evidence', 'cluster_id'];
const EVIDENCE_LABELS = { problem_confidence: 'Conf.', problem_evidence: 'Evidence', cluster_id: 'Cluster', url: 'URL',
  pages_mentioning: 'Pages', wording_variants: 'Variants', avg_confidence: 'Avg conf.' };

const evidenceRender = {
  url: (r: any) => isUrl(r.url) ? <Ext href={r.url}>{String(r.url).replace(/^https?:\/\//, '').slice(0, 50)}</Ext> : '—',
  problem_evidence: (r: any) => {
    const s = String(r.problem_evidence ?? '');
    return <span title={s}>{s.length > 90 ? `${s.slice(0, 90)}…` : (s || '—')}</span>;
  },
  problem_confidence: (r: any) => n(r.problem_confidence),
  avg_confidence: (r: any) => n(r.avg_confidence),
};

const CompanyDrawer: React.FC<{ row: Record<string, any>; onClose: () => void }> = ({ row, onClose }) => {
  const domain = String(row.domain || '').toLowerCase();
  const [cluster, setCluster] = useState<string>('');
  const d = useOcean(() => oceanGet('company', { domain }), [domain]);
  // The company read caps evidence at 300 rows in cluster order, so for a
  // big company (143 of them have more) only the lowest clusters are in
  // it; narrowing that list client-side showed "0 in cluster N" for most
  // problems. A selected cluster is fetched on its own, like ProblemDrawer.
  const ev = useOcean(() => oceanGet('evidence', {
    domain, cluster_id: cluster, page: 1, per_page: 200, sort: 'problem_confidence', direction: 'desc',
  }), [domain, cluster], !!cluster);
  const data = d.data || {};
  // The row the drawer was opened from fills the header while the read runs.
  const company = { ...row, ...(data.company || {}) };
  const dom = data.domain_info || {};
  const problems: any[] = data.problems || [];
  const evidence: any[] = data.evidence || [];
  const shown: any[] = cluster ? (ev.data?.rows || []) : evidence;
  const shownTotal = cluster ? Number(ev.data?.total ?? shown.length) : Number(data.evidence_total ?? evidence.length);
  const ps = data.pages_summary || {};

  return (
    <Drawer title={company.company_name || domain}
      subtitle={<Ext href={`https://${domain}`}>{domain}</Ext>}
      onClose={onClose}
      right={<ExportPair params={{ view: 'evidence', domain }} name={`ocean_${domain}_evidence`}
        title="Every evidence row for this company" />}>
      <KPIRow>
        <KPI label="Unique problems" value={n(company.unique_problems)} />
        <KPI label="Raw statements" value={n(company.raw_statements)} />
        <KPI label="Avg confidence" value={n(company.avg_confidence)} />
        <KPI label="URLs" value={n(company.distinct_urls)} />
        <KPI label="Pages classified" value={n(ps.total)} hint={ps.problems !== undefined ? `${n(ps.problems)} with a problem` : undefined} />
      </KPIRow>
      <Section title="Company">
        <div className="flex flex-col divide-y divide-grayscale-200">
          <Fact label="Industry" value={company.industry ?? dom.industry} />
          <Fact label="Business model" value={company.business_model ?? dom.business_model} />
          <Fact label="HQ country" value={company.hq_country_norm || company.hq_country} />
          <Fact label="Band" value={company.problem_band} />
          <Fact label="Page types" value={company.page_types} />
          <Fact label="Top tags" value={company.top_technical_tags} />
          <Fact label="Dedup reduction" value={company.dedup_reduction_pct !== undefined && company.dedup_reduction_pct !== null ? `${n(company.dedup_reduction_pct)}%` : undefined} />
          {dom.industry_2 && <Fact label="Industry 2 (batch-1)" value={dom.industry_2} />}
          {dom.confidence !== undefined && dom.confidence !== null && <Fact label="Classification confidence" value={n(dom.confidence)} />}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Flag on={company.has_email}>email</Flag>
          <Flag on={company.has_phone}>phone</Flag>
          <Flag on={company.has_linkedin}>LinkedIn</Flag>
          <Flag on={company.has_contact_person}>contact person</Flag>
        </div>
        {!!(ps.statuses?.length || ps.page_types?.length) && (
          <p className="text-xs text-grayscale-1100">
            Pages: {(ps.page_types || []).map((x: any) => `${x.value} ${n(x.n)}`).join(' · ')}
            {ps.statuses?.length ? <> — status {(ps.statuses || []).map((x: any) => `${x.value} ${n(x.n)}`).join(' · ')}</> : null}
          </p>
        )}
      </Section>
      {d.error && <Msg kind="error">{d.error}</Msg>}
      {d.loading ? <Loading label="Loading problems and evidence…" /> : (
        <>
          <Section title={`Unique problems (${problems.length})`}
            right={cluster ? <Btn small onClick={() => setCluster('')}>Show all evidence</Btn> : undefined}>
            <p className="text-xs text-grayscale-1100 -mt-1">
              One row per cluster; pages mentioning is supporting weight, not a separate problem.
              "Evidence" narrows the list below to that cluster.
            </p>
            <DataTable rows={problems} columns={PROBLEM_COLS.filter(c => !problems[0] || c in problems[0])}
              labels={EVIDENCE_LABELS} maxHeight="20rem" csvName={`ocean_${domain}_problems`} render={evidenceRender}
              actions={r => (
                <Btn small onClick={() => setCluster(String(r.cluster_id ?? ''))}>
                  {cluster && String(r.cluster_id ?? '') === cluster ? 'Showing' : 'Evidence'}
                </Btn>
              )} />
          </Section>
          <Section title={`Evidence (${n(shownTotal)}${cluster ? ` in cluster ${cluster}` : ''})`}>
            {cluster && ev.error && <Msg kind="error">{ev.error}</Msg>}
            {shownTotal > shown.length && !(cluster && ev.loading) && (
              <p className="text-xs text-amber-700 -mt-1">
                {cluster
                  ? <>Showing the {n(shown.length)} highest-confidence rows of {n(shownTotal)} — Export above has all of them.</>
                  : <>Showing the first {n(shown.length)} of {n(shownTotal)} evidence rows — Export above has all of them.</>}
              </p>
            )}
            {cluster && ev.loading ? <Loading /> : (
              <DataTable rows={shown} columns={EVIDENCE_COLS.filter(c => !shown[0] || c in shown[0])}
                labels={EVIDENCE_LABELS} maxHeight="24rem" csvName={`ocean_${domain}_evidence`}
                render={evidenceRender} />
            )}
          </Section>
        </>
      )}
      {typeof company.problem_statements === 'string' && company.problem_statements && (
        <Section title="Consolidated list (as exported to the workbook)">
          <pre className="text-xs text-grayscale-1300 whitespace-pre-wrap break-words">{company.problem_statements}</pre>
        </Section>
      )}
    </Drawer>
  );
};

const ProblemDrawer: React.FC<{
  row: Record<string, any>; onClose: () => void; onCompany: (r: Record<string, any>) => void;
}> = ({ row, onClose, onCompany }) => {
  const domain = String(row.domain || '');
  const cluster = String(row.cluster_id ?? '');
  const d = useOcean(() => oceanGet('evidence', {
    domain, cluster_id: cluster, page: 1, per_page: 200, sort: 'problem_confidence', direction: 'desc',
  }), [domain, cluster]);
  const ev: any[] = d.data?.rows || [];
  const evTotal = Number(d.data?.total ?? ev.length);
  const urls = String(row.sample_urls || '').split(/[,\s]+/).filter(isUrl);
  return (
    <Drawer title={row.problem_statement || `Cluster ${cluster}`}
      subtitle={<>{row.company_name || domain} · <Ext href={`https://${domain}`}>{domain}</Ext> · cluster {cluster}</>}
      onClose={onClose}
      right={<Btn small onClick={() => onCompany(row)}>Open company</Btn>}>
      <KPIRow>
        <KPI label="Pages mentioning" value={n(row.pages_mentioning)} />
        <KPI label="Wording variants" value={n(row.wording_variants)} />
        <KPI label="Avg confidence" value={n(row.avg_confidence)} />
      </KPIRow>
      <Section title="Problem">
        <div className="flex flex-col divide-y divide-grayscale-200">
          <Fact label="Technical tags" value={row.technical_tags} />
          <Fact label="Page types" value={row.page_types} />
          <Fact label="Industry" value={row.industry} />
          <Fact label="Business model" value={row.business_model} />
          <Fact label="HQ country" value={row.hq_country_norm} />
        </div>
        {!!urls.length && (
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-grayscale-1100">Sample URLs</span>
            {urls.map(u => <Ext key={u} href={u} />)}
          </div>
        )}
      </Section>
      <Section title={`Evidence — every page that said it (${n(evTotal)})`}
        right={<ExportPair params={{ view: 'evidence', domain, cluster_id: cluster }} name={`ocean_${domain}_c${cluster}`} />}>
        {d.error && <Msg kind="error">{d.error}</Msg>}
        {evTotal > ev.length && (
          <p className="text-xs text-amber-700 -mt-1">
            Showing the {n(ev.length)} highest-confidence rows of {n(evTotal)} — Export has all of them.
          </p>
        )}
        {d.loading ? <Loading /> : (
          <DataTable rows={ev} columns={EVIDENCE_COLS.filter(c => c !== 'cluster_id' && (!ev[0] || c in ev[0]))}
            labels={EVIDENCE_LABELS} maxHeight="26rem" csvName={`ocean_${domain}_c${cluster}_evidence`}
            render={evidenceRender} />
        )}
      </Section>
    </Drawer>
  );
};

const RowDrawer: React.FC<{
  kind: DrawerKind; row: Record<string, any>; onClose: () => void; onCompany: (r: Record<string, any>) => void;
}> = ({ kind, row, onClose, onCompany }) => {
  const title = kind === 'evidence' ? (row.problem_statement || 'Evidence row')
    : kind === 'page' ? (row.url ? String(row.url).replace(/^https?:\/\//, '') : 'Page')
    : String(row.domain || 'Domain');
  const subtitle = kind === 'domain'
    ? `${row.industry || '—'} · ${row.business_model || '—'}`
    : <>{row.company_name || row.domain} · <Ext href={`https://${row.domain}`}>{row.domain}</Ext></>;
  const canOpen = kind !== 'domain' || truthy(row.has_problems);
  // The list view previews problem_evidence at 600 chars (evidence_chars is
  // the real length); the company view and the exports carry the whole text.
  const clipped = kind === 'evidence' && typeof row.evidence_chars === 'number'
    && row.evidence_chars > String(row.problem_evidence || '').length;
  return (
    <Drawer title={title} subtitle={subtitle} onClose={onClose}
      right={canOpen ? <Btn small onClick={() => onCompany(row)}>Open company</Btn> : undefined}>
      {kind === 'evidence' && isUrl(row.url) && (
        <Section title="Source page"><Ext href={row.url} /></Section>
      )}
      {clipped && (
        <p className="text-xs text-amber-700">
          Evidence preview only ({n(row.evidence_chars)} characters in full) — open the company for the whole text.
        </p>
      )}
      <Section title={kind === 'evidence' ? 'Statement, evidence and every field' : 'Every field'}>
        <FieldList row={row} skip={['evidence_chars']} />
      </Section>
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// 4. Dashboards — the pivot registry grouped D1..D8, one pivot as a table
// ---------------------------------------------------------------------------
const DASHBOARDS: Record<string, string> = {
  D1: 'Dataset overview', D2: 'Problem landscape', D3: 'Technology demand', D4: 'Industry analysis',
  D5: 'Geography', D6: 'Company coverage', D7: 'Crawl & extraction quality', D8: 'Company problem distribution',
};

const dashKey = (r: any): string => {
  const raw = String(pick(r, 'dashboard') ?? pick(r, 'key') ?? '');
  const m = /D(?:ashboard)?[\s_-]*(\d)/i.exec(raw);
  return m ? `D${m[1]}` : (raw || 'Other');
};

const Dashboards: React.FC = () => {
  const pv = useOcean(() => oceanGet('pivots'), []);
  const [key, setKey] = useState('');
  const all: any[] = useMemo(() => pv.data?.rows || [], [pv.data]);
  const groups = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const r of all) (g[dashKey(r)] ||= []).push(r);
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [all]);
  const selected = all.find(r => String(r.key) === key);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Pivot tables — the 7 dashboards + the company distribution"
        right={<span className="text-xs text-grayscale-1100">{all.length} pivots</span>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Every pivot from the "Ocean Data Pivot Tables" spec, as loaded into DuckDB. Row-level dumps
          (D8 detail sheets, D2 statements, D4 industry × statement) are registered but not loaded as tables —
          their rows are the Evidence and Companies tabs; download the source CSV instead. The dashboard
          workbooks themselves are on the Data &amp; files tab.
        </p>
        {pv.error && <Msg kind="error">{pv.error}</Msg>}
        {pv.loading ? <Loading /> : (
          <div className="grid md:grid-cols-2 gap-3">
            {groups.map(([g, rows]) => (
              <div key={g} className="rounded-lg border border-grayscale-300 p-3 flex flex-col gap-1">
                <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
                  {g}{DASHBOARDS[g] ? ` — ${DASHBOARDS[g]}` : ''}
                </div>
                {rows.map(r => {
                  const k = String(r.key);
                  return (
                    <button key={k} type="button" onClick={() => setKey(k)}
                      className={`text-left text-sm rounded-md px-2 py-1 flex items-center justify-between gap-2 ${
                        key === k ? 'bg-primary-50 ring-1 ring-primary-300' : 'hover:bg-grayscale-200'}`}>
                      <span className="truncate">{r.title || k}</span>
                      <span className="text-xs text-grayscale-1100 whitespace-nowrap">
                        {truthy(r.is_table) ? `${n(r.row_count)} rows` : `file only · ${n(r.row_count)} rows`}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Section>
      {selected && <Pivot key={key} row={selected} />}
    </div>
  );
};

const Pivot: React.FC<{ row: any }> = ({ row }) => {
  const key = String(row.key);
  const isTable = truthy(row.is_table);
  const d = useOcean(() => oceanGet('pivot', { key }), [key], isTable);
  const data = useMemo(() => normRows(d.data), [d.data]);
  const [err, setErr] = useState('');
  // The registry stores source_file either bare or already under pivots/
  // (the sync script writes "pivots/<name>"); file-only pivots never call
  // the handler, so normalise here the way admin_ocean._pivot_file does or
  // the download asks for pivots/pivots/<name> and 404s.
  const sf = String(row.source_file || '');
  const file = d.data?.file || (sf ? (sf.includes('/') ? sf : `pivots/${sf}`) : '');
  const grabSource = async () => {
    setErr('');
    const e = await fetchFile(FILE_PATH, { name: file }, basename(sf) || `${key}.csv`);
    if (e) setErr(e);
  };
  return (
    <Section title={row.title || key}
      right={<div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-grayscale-1100 font-mono">{row.source_file || key}</span>
        {isTable && <ExportPair params={{ view: 'pivot', key }} name={key} title="This pivot, every row" />}
        {!!file && <Btn small onClick={grabSource}>Download source CSV</Btn>}
      </div>}>
      {err && <Msg kind="error">{err}</Msg>}
      {!isTable ? (
        <p className="text-sm text-grayscale-1100">
          Registered as a file only ({n(row.row_count)} rows) — the same rows are browsable on the Evidence /
          Companies tabs. Use "Download source CSV" for the file.
        </p>
      ) : d.error ? <Msg kind="error">{d.error}</Msg>
        : d.loading ? <Loading />
        : <DataTable rows={data.rows} columns={data.columns} csvName={key} searchable maxHeight="34rem" />}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// 5. Data & files — where everything lives, and the copies on this server
// ---------------------------------------------------------------------------
const DS_COLS = ['title', 'kind', 'location', 'size_bytes', 'objects', 'rows', 'present'];
const DS_LABELS = { size_bytes: 'Size', present: 'On server' };

const DataFiles: React.FC<{ aws: any; manifest: any }> = ({ aws, manifest }) => {
  const ds = useOcean(() => oceanGet('datasets'), []);
  const fl = useOcean(() => oceanGet('files'), []);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [desc, setDesc] = useState('');

  const rows: any[] = useMemo(() => ds.data?.rows || [], [ds.data]);
  const stages = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const r of rows) (g[String(r.stage || 'Other')] ||= []).push(r);
    // "1 acquisition" before "10 admin-sync" whatever order the registry listed them in.
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [rows]);
  const files: any[] = useMemo(() => fl.data?.rows || [], [fl.data]);
  const disk = ds.data?.disk || {};

  const grab = async (r: any) => {
    const k = String(r.key);
    setBusy(k); setErrs(e => ({ ...e, [k]: '' }));
    if (truthy(r.present) && r.local_path) {
      const e = await fetchFile(FILE_PATH, { name: r.local_path }, basename(r.local_path));
      if (e) setErrs(x => ({ ...x, [k]: e }));
    } else {
      // Big or S3-only artefacts are never proxied through the API box: the
      // server signs a 1-hour link and the browser fetches from S3 directly.
      const { data, error } = await oceanGet('presign', { key: r.key, s3_key: r.s3_key || '' });
      const url = data?.url;
      if (url && !error) { setLinks(l => ({ ...l, [k]: url })); window.open(url, '_blank', 'noopener'); }
      else setErrs(x => ({ ...x, [k]: error || 'No link returned' }));
    }
    setBusy('');
  };

  const grabFile = async (r: any) => {
    const p = String(r.path);
    setBusy(p); setErrs(e => ({ ...e, [p]: '' }));
    const e = await fetchFile(FILE_PATH, { name: p }, basename(p));
    if (e) setErrs(x => ({ ...x, [p]: e }));
    setBusy('');
  };

  const dsRender = {
    title: (r: any) => (
      <button type="button" onClick={() => setDesc(desc === r.key ? '' : r.key)}
        title={r.description || ''} className="text-left underline-offset-2 hover:underline">
        {r.title}
      </button>
    ),
    location: (r: any) => <span className="font-mono text-xs" title={r.location}>{r.location}</span>,
    size_bytes: (r: any) => fmtBytes(r.size_bytes),
    objects: (r: any) => n(r.objects),
    rows: (r: any) => n(r.rows),
  };
  const dsActions = (r: any) => {
    const k = String(r.key);
    const local = truthy(r.present) && !!r.local_path && !/\.duckdb$|manifest\.json$/.test(String(r.local_path));
    const remote = !local && truthy(r.downloadable) && !!r.s3_key;
    return (
      <div className="flex items-center gap-2">
        {(local || remote) && (
          <Btn small disabled={busy === k} onClick={() => grab(r)}
            title={local ? 'Download the copy on this server' : 'Get a 1-hour S3 link'}>
            {busy === k ? 'Working…' : local ? 'Download' : 'S3 link'}
          </Btn>
        )}
        {links[k] && <Ext href={links[k]}>open link</Ext>}
        {errs[k] && <span className="text-xs text-red-600">{errs[k]}</span>}
      </div>
    );
  };
  const described = rows.find(r => r.key === desc);

  const docs = files.filter(f => f.kind === 'docs');
  const workbooks = files.filter(f => f.kind === 'workbooks');
  const fileRender = {
    path: (r: any) => <span className="font-mono text-xs">{r.path}</span>,
    size: (r: any) => fmtBytes(r.size),
    mtime: (r: any) => fmtCell('mtime_at', r.mtime),
  };
  const fileActions = (r: any) => {
    const p = String(r.path);
    return (
      <div className="flex items-center gap-2">
        {truthy(r.downloadable) && <Btn small disabled={busy === p} onClick={() => grabFile(r)}>Download</Btn>}
        {errs[p] && <span className="text-xs text-red-600">{errs[p]}</span>}
      </div>
    );
  };
  const FileRows: React.FC<{ list: any[]; name: string; empty: string }> = ({ list, name, empty }) => (
    <DataTable rows={list} columns={['path', 'size', 'mtime']} labels={{ mtime: 'Modified' }}
      render={fileRender} actions={fileActions} csvName={name} maxHeight="24rem" empty={empty} />
  );

  return (
    <div className="flex flex-col gap-4">
      <Section title="AWS">
        <div className="grid md:grid-cols-2 gap-x-8">
          <Fact label="Account" value={aws.account} />
          <Fact label="Region" value={aws.region} />
          <Fact label="Bucket" value={<span className="font-mono">{aws.bucket}</span>} />
          <Fact label="Athena database" value={<span className="font-mono">{aws.athena_database}</span>} />
          {aws.athena_workgroup && <Fact label="Athena workgroup" value={aws.athena_workgroup} />}
          {aws.athena_results && <Fact label="Athena results" value={<span className="font-mono">{aws.athena_results}</span>} />}
          {aws.iam_user && <Fact label="IAM user" value={aws.iam_user} />}
          {aws.worker_role && <Fact label="Worker role" value={aws.worker_role} />}
          {aws.cloudformation_stack && <Fact label="CloudFormation stack" value={aws.cloudformation_stack} />}
          {aws.budget && <Fact label="Budget" value={aws.budget} />}
        </div>
        <p className="text-xs text-grayscale-1100">
          {aws.credentials || 'Credentials never leave the server; anything not present locally is fetched by a signed S3 link.'}
        </p>
      </Section>

      <Section title="Where every artefact lives"
        right={<span className="text-xs text-grayscale-1100">
          {rows.length} entries
          {disk.used_bytes !== undefined && <> · ocean_data/ {fmtBytes(disk.used_bytes)} on disk</>}
          {disk.db_bytes !== undefined && <> · DuckDB {fmtBytes(disk.db_bytes)}</>}
          {disk.volume_free_bytes !== undefined && <> · {fmtBytes(disk.volume_free_bytes)} free of {fmtBytes(disk.volume_total_bytes)}</>}
        </span>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The dataset inventory (S3 prefixes, Athena tables, exports) plus everything
          under ocean_data/ on this server. Click a title for its description. "Download" serves the local
          copy; "S3 link" signs a one-hour URL for what only exists in the bucket.
        </p>
        {described && (
          <Msg kind="ok">
            <span className="font-medium">{described.title}</span> — {described.description || 'no description'}
            {Array.isArray(described.columns) && described.columns.length > 0 && (
              <span className="block font-mono text-xs mt-1">{described.columns.join(', ')}</span>
            )}
          </Msg>
        )}
        {ds.error && <Msg kind="error">{ds.error}</Msg>}
        {ds.loading ? <Loading /> : stages.map(([stage, list]) => (
          <div key={stage} className="flex flex-col gap-1">
            <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{stage}</div>
            <DataTable rows={list} columns={DS_COLS.filter(c => !list[0] || c in list[0])}
              labels={DS_LABELS} render={dsRender} actions={dsActions} maxHeight="30rem" />
          </div>
        ))}
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Workbooks on this server"
          right={<span className="text-xs text-grayscale-1100">xlsx pulled from S3</span>}>
          {fl.loading ? <Loading /> : <FileRows list={workbooks} name="ocean_workbooks" empty="No workbooks/ folder in this build." />}
          {!!manifest?.big_not_pulled?.length && (
            <p className="text-xs text-grayscale-1100">
              Not pulled (over 100 MB, use the S3 link above): {manifest.big_not_pulled.map((p: string) => basename(p)).join(', ')}
            </p>
          )}
        </Section>
        <Section title="Docs"
          right={<span className="text-xs text-grayscale-1100">dataset notes, pivot definitions, prompt, tag taxonomy</span>}>
          {fl.loading ? <Loading /> : <FileRows list={docs} name="ocean_docs" empty="No docs/ folder in this build." />}
        </Section>
      </div>

      <Section title="Everything under ocean_data/ on this server"
        right={<span className="text-xs text-grayscale-1100 font-mono">{fl.data?.data_dir || ''}</span>}>
        {fl.error && <Msg kind="error">{fl.error}</Msg>}
        {!!manifest?.missing?.length && (
          <p className="text-xs text-amber-700">
            Expected but missing: {manifest.missing.join(', ')}
          </p>
        )}
        {fl.loading ? <Loading /> : (
          <DataTable rows={files} columns={['path', 'kind', 'size', 'mtime']} labels={{ mtime: 'Modified' }}
            render={fileRender} actions={fileActions} searchable csvName="ocean_files" maxHeight="30rem"
            empty="The folder is empty — run scripts/sync_ocean_data.py on the server." />
        )}
        <p className="text-xs text-grayscale-1100">
          {n(fl.data?.count)} files · {fmtBytes(fl.data?.total_bytes)}. The database file and the manifest are not downloadable.
        </p>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 6. Schema — every DuckDB table with its row count and columns
// ---------------------------------------------------------------------------
const Schema: React.FC = () => {
  const sc = useOcean(() => oceanGet('schema'), []);
  const tables: any[] = useMemo(() => sc.data?.tables || [], [sc.data]);
  const [openT, setOpenT] = useState('');
  const db = sc.data?.database;

  return (
    <div className="flex flex-col gap-4">
      <Section title="DuckDB tables"
        right={<span className="text-xs text-grayscale-1100 font-mono">
          {sc.data?.path || 'ocean.duckdb'}{db?.database_size ? ` · ${db.database_size}` : ''}
        </span>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The file the API reads, opened read-only per request. companies / problems / evidence are the
          product dataset; pages is the unified base behind every pivot; domains is the
          batch-1 classification; pivot_* are the small dashboard CSVs; datasets and meta are the inventory.
        </p>
        {sc.error && <Msg kind="error">{sc.error}</Msg>}
        {sc.loading ? <Loading /> : (
          <DataTable rows={tables.map(t => ({ table: t.name, rows: t.rows, columns: (t.columns || []).length }))}
            columns={['table', 'rows', 'columns']} maxHeight="30rem" csvName="ocean_schema"
            render={{ rows: r => n(r.rows), table: r => <span className="font-mono">{r.table}</span> }}
            actions={r => <Btn small onClick={() => setOpenT(v => v === r.table ? '' : r.table)}>
              {openT === r.table ? 'Hide columns' : 'Columns'}
            </Btn>} />
        )}
      </Section>
      {tables.filter(t => !openT || t.name === openT).map(t => (
        <Section key={t.name} title={`${t.name} — ${n(t.rows)} rows`}>
          <DataTable rows={t.columns || []} columns={['name', 'type']} maxHeight="24rem"
            render={{ name: r => <span className="font-mono">{r.name}</span> }} />
        </Section>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 7. SQL — read-only DuckDB console with export of the query itself
// ---------------------------------------------------------------------------
const DEFAULT_SQL = 'SELECT industry, COUNT(*) n FROM companies GROUP BY 1 ORDER BY 2 DESC';
const TABLE_NAMES = ['companies', 'problems', 'evidence', 'pages', 'domains', 'tags', 'pivots', 'pivot_<key>', 'datasets', 'meta'];
const PRESETS: { label: string; query: string }[] = [
  { label: 'Companies per industry', query: DEFAULT_SQL },
  { label: 'Top companies by unique problems',
    query: 'SELECT domain, company_name, industry, unique_problems, raw_statements, avg_confidence\nFROM companies\nORDER BY unique_problems DESC\nLIMIT 100' },
  { label: 'Problems per tag',
    query: "SELECT t.tag, COUNT(*) AS problems\nFROM problems p, tags t\nWHERE p.technical_tags LIKE '%' || t.tag || '%'\nGROUP BY 1 ORDER BY 2 DESC" },
  { label: 'Inference rate by page type',
    query: 'SELECT page_type, COUNT(*) AS pages, SUM(CASE WHEN is_problem THEN 1 ELSE 0 END) AS problems,\n       ROUND(100.0 * SUM(CASE WHEN is_problem THEN 1 ELSE 0 END) / COUNT(*), 1) AS rate_pct\nFROM pages GROUP BY 1 ORDER BY 2 DESC' },
  { label: 'Countries by company', query: 'SELECT hq_country_norm, COUNT(*) n FROM companies\nWHERE hq_country_norm IS NOT NULL GROUP BY 1 ORDER BY 2 DESC LIMIT 50' },
];

const SqlConsole: React.FC = () => {
  const [query, setQuery] = useState(DEFAULT_SQL);
  const [limit, setLimit] = useState('500');
  const [result, setResult] = useState<any>(null);
  const [ran, setRan] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (sql: string = query) => {
    setBusy(true); setErr(''); setResult(null);
    const { data, error } = await panelPost(S, 'sql', { query: sql, limit: Number(limit) || 500 });
    setBusy(false);
    const e = error || (data && typeof data === 'object' && data.ready === false ? data.message : '') || data?.error || '';
    if (e) setErr(String(e)); else { setResult(data); setRan(sql); }
  };
  // SELECT a, a would collapse into one property, so repeated names are suffixed.
  const cols: string[] = useMemo(() => {
    const raw: any[] = Array.isArray(result?.columns) ? result.columns : [];
    const seen: Record<string, number> = {};
    return raw.map((c: any) => {
      const name = String(c);
      seen[name] = (seen[name] || 0) + 1;
      return seen[name] > 1 ? `${name} (${seen[name]})` : name;
    });
  }, [result]);
  const rows = useMemo(() => {
    const src: any[] = result?.rows || [];
    if (src.length && Array.isArray(src[0])) return src.map(r => Object.fromEntries(cols.map((c, i) => [c, r[i]])));
    return src as Record<string, any>[];
  }, [result, cols]);

  return (
    <Section title="SQL — DuckDB, read-only"
      right={result ? <ExportPair params={{ view: 'sql', query: ran, limit: Number(limit) || 500 }} name="ocean_query"
        title="Runs this query again on the server and streams every row up to the limit" /> : undefined}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        One SELECT / WITH statement against ocean.duckdb, opened read-only with external file access off and a
        30 s watchdog; at most 5,000 rows here (raise the limit or export for more). Tables:{' '}
        {TABLE_NAMES.map((t, i) => <React.Fragment key={t}>{i ? ' · ' : ''}<span className="font-mono">{t}</span></React.Fragment>)}.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-grayscale-1100">Starter queries:</span>
        {PRESETS.map(p => (
          <Btn key={p.label} small onClick={() => { setQuery(p.query); run(p.query); }}>{p.label}</Btn>
        ))}
      </div>
      <textarea value={query} rows={6} onChange={e => setQuery(e.target.value)} spellCheck={false}
        className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm font-mono" />
      <div className="flex items-center gap-2 flex-wrap">
        <Btn kind="primary" disabled={busy || !query.trim()} onClick={() => run()}>{busy ? 'Running…' : 'Run'}</Btn>
        <span className="text-xs text-grayscale-1100">Limit</span>
        <TextInput value={limit} onChange={setLimit} type="number" placeholder="500" />
        {result && (
          <span className="text-xs text-grayscale-1100">
            {n(result.row_count ?? rows.length)} row{rows.length === 1 ? '' : 's'}
            {result.elapsed_ms !== undefined ? ` · ${n(result.elapsed_ms)} ms` : ''}
          </span>
        )}
        {result?.truncated && (
          <span className="text-xs text-amber-700">
            Truncated at the limit — raise it, add a LIMIT, or export to get every row.
          </span>
        )}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      {result && (
        <DataTable rows={rows} columns={cols} csvName="ocean_query" searchable maxHeight="34rem"
          empty="Query ran and returned no rows." />
      )}
    </Section>
  );
};

export default OceanPanel;
